import {
  Box,
  Divider,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  StyleProps,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { HiOutlineInformationCircle } from "react-icons/hi2";

import { DeepPick } from "../../../../types";
import { AiInterviewEvaluationFragment } from "../../../graphql";

type InterviewEvaluation = Pick<AiInterviewEvaluationFragment, "finalScore"> &
  DeepPick<
    AiInterviewEvaluationFragment,
    "questions",
    "id" | "question" | "points"
  >;

type EvaluationScorePopoverProps = {
  evaluation: InterviewEvaluation;
};

export const EvaluationScorePopover: React.FC<EvaluationScorePopoverProps> = ({
  evaluation,
}) => {
  const questions = evaluation.questions ?? [];
  const scoreQuestions = questions.filter((q) => q.points);
  const customWeightsEnabled = scoreQuestions.some(
    (q) => q.question.customWeight
  );

  const numericColumn: StyleProps = {
    w: "16",
    textAlign: "right",
  };

  return (
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Flex
          align="center"
          justify="center"
          gap="1"
          fontSize="xs"
          color="gray.600"
          textAlign="center"
        >
          Overall score
          <Icon as={HiOutlineInformationCircle} boxSize="4" color="gray.500" />
        </Flex>
      </PopoverTrigger>
      <PopoverContent
        boxShadow="md"
        w="auto"
        maxW="min(560px, 90vw)"
        bg="gray.600"
        color="white"
      >
        <PopoverBody px="4" pb="4" pt="3" fontSize="xs" color="white">
          <Flex gap="4" color="gray.200">
            <Box flex="1" minW="0" alignSelf="end">
              Question
            </Box>
            <Box {...numericColumn}>
              Score
              <br />
              (1 – 4)
            </Box>
            <Box {...numericColumn}>
              Weight
              <br />
              (100%)
            </Box>
            <Box {...numericColumn}>
              Weighted
              <br />
              score (100)
            </Box>
          </Flex>

          <Divider borderColor="gray.400" my="3" />

          <Flex
            direction="column"
            gap="2"
            fontWeight="medium"
            maxH="60"
            overflowY="auto"
            pr="3.5"
            mr="-3.5"
          >
            {questions.map((q) => {
              // Rubric score: 1-4
              const score = q.points;

              // Weight: 1-100
              const defaultWeight = scoreQuestions.length
                ? 100 / scoreQuestions.length
                : 0;
              let weight = score ? defaultWeight : 0;
              if (customWeightsEnabled) {
                weight = q.question?.customWeight || 0;
              }
              const weightPercent = weight ? `${weight.toFixed(1)}%` : "";

              // Weighted score: 1-100
              const weightedScore =
                weight && score ? ((score * weight) / 4).toFixed(1) : "";

              return (
                <Flex key={q.id} gap="4">
                  <Box flex="1" minW="0">
                    <Text
                      fontSize="xs"
                      color="white"
                      fontWeight="medium"
                      lineHeight="4"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {q.question.question || ""}
                    </Text>
                  </Box>
                  <Box {...numericColumn}>
                    <Text
                      fontSize="xs"
                      color="white"
                      fontWeight="medium"
                      lineHeight="4"
                    >
                      {score ? score.toFixed(1) : ""}
                    </Text>
                  </Box>
                  <Box {...numericColumn}>
                    <Text
                      fontSize="xs"
                      color="white"
                      fontWeight="medium"
                      lineHeight="4"
                    >
                      {weightPercent}
                    </Text>
                  </Box>
                  <Box {...numericColumn}>
                    <Text
                      fontSize="xs"
                      color="white"
                      fontWeight="medium"
                      lineHeight="4"
                    >
                      {weightedScore}
                    </Text>
                  </Box>
                </Flex>
              );
            })}
          </Flex>

          <Divider borderColor="gray.400" my="3" />

          <Flex gap="2" fontWeight="semibold">
            <Box flex="1" minW="0">
              <Text
                fontSize="xs"
                color="white"
                fontWeight="semibold"
                lineHeight="4"
              >
                Overall candidate score (rounded)
              </Text>
            </Box>
            <Box w="16" />
            <Box w="16" />
            <Box {...numericColumn}>
              <Text
                fontSize="xs"
                color="white"
                fontWeight="semibold"
                lineHeight="4"
              >
                {evaluation.finalScore}
              </Text>
            </Box>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
